// Сметките зад справка „Сторно" — обобщенията за трите изгледа.
//
// Един ред от BE = един сторниран продукт. Оттук се вадят трите гледни точки:
// списък (както е дошло), по сервитьор и по продукт.

import { parseDecimal } from "../../../../components/dataTable";
import type { CancelledItem } from "../../../../api/reportsCancellations";

/**
 * Ред със ВЕЧЕ РАЗРЕШЕНИ имена.
 *
 * Имената НЕ бива да се вадят от справочник вътре в `accessorFn`: TanStack
 * кешира стойността на клетката в реда за живота на `data`, а React Query пази
 * същата референция, когато отговорът е непроменен (structural sharing).
 * Справочник, който пристига по-късно (меню, потребители), тогава остава
 * невидим и клетката показва „#15" завинаги. Затова имената влизат в САМИТЕ
 * данни — сменят ли се, сменя се и масивът, и редовият модел се преизгражда.
 */
export type NamedCancelledItem = CancelledItem & {
  productName: string;
  waiterName: string;
  cancelledByName: string;
};

/** Обобщение за КПИ лентата. */
export type CancellationsSummary = {
  rows: number;
  bills: number;
  total: number;
  average: number;
  /** Редове с ДОКАЗАНО минаване през кухнята. */
  afterKitchen: number;
  /** Редове, отбелязани като БРАК — другите са обикновено сторно. */
  defective: number;
  /**
   * Редове, за които няма нито един кухненски запис. Ако това е всичко, което
   * имаме, показателят „след кухня" не значи нищо и не се показва.
   */
  kitchenUnknown: number;
};

export const summarize = (items: CancelledItem[]): CancellationsSummary => {
  const bills = new Set<number>();
  let total = 0;
  let afterKitchen = 0;
  let kitchenUnknown = 0;
  let defective = 0;

  for (const item of items) {
    bills.add(item.bill_id);
    total += parseDecimal(item.total_sum);
    if (item.is_defective) defective += 1;
    if (item.after_kitchen === true) afterKitchen += 1;
    if (item.after_kitchen === null || item.after_kitchen === undefined) {
      kitchenUnknown += 1;
    }
  }

  return {
    rows: items.length,
    bills: bills.size,
    total,
    average: items.length ? total / items.length : 0,
    afterKitchen,
    defective,
    kitchenUnknown,
  };
};

/** Ред в изгледа „По сервитьор" — по сервитьора НА СМЕТКАТА. */
export type WaiterVoidRow = {
  /** `null` = сметката няма записан сервитьор (стар запис, служебна сметка). */
  waiterId: number | null;
  bills: number;
  rows: number;
  afterKitchen: number;
  total: number;
  /** Делът от оборота на този сервитьор за периода; `null` при нулев оборот. */
  shareOfRevenue: number | null;
};

export const groupByWaiter = (items: CancelledItem[]): WaiterVoidRow[] => {
  const map = new Map<
    number | null,
    { bills: Set<number>; rows: number; afterKitchen: number; total: number; revenue: number }
  >();

  for (const item of items) {
    // САМО сервитьорът на сметката. Падането към „кой е натиснал сторното"
    // приписваше чуждото сторно на управителя и смяташе дела спрямо НЕГОВИЯ
    // оборот — числото, по което се съди за кражби, сочеше грешния човек.
    const waiterId = item.waiter_id ?? null;
    const entry = map.get(waiterId) ?? {
      bills: new Set<number>(),
      rows: 0,
      afterKitchen: 0,
      total: 0,
      revenue: parseDecimal(item.waiter_period_revenue),
    };
    entry.bills.add(item.bill_id);
    entry.rows += 1;
    if (item.after_kitchen === true) entry.afterKitchen += 1;
    entry.total += parseDecimal(item.total_sum);
    // Оборотът е един и същ по всички редове на сервитьора — взима се веднъж.
    entry.revenue = Math.max(entry.revenue, parseDecimal(item.waiter_period_revenue));
    map.set(waiterId, entry);
  }

  return Array.from(map.entries())
    .map(([waiterId, entry]) => ({
      waiterId,
      bills: entry.bills.size,
      rows: entry.rows,
      afterKitchen: entry.afterKitchen,
      total: entry.total,
      // Брутото на сервитьора = платеното + сторнираното: делът отговаря на
      // „колко от това, което е маркирал, е отпаднало".
      //
      // Знаменателят иска ЗАКРИТ оборот (BE брои само `status == "closed"`).
      // Сторно по още отворена маса значи нула закрит оборот — а тогава
      // формулата дава 100 % и изкарва сервитьора виновен за нищо. Без такъв
      // оборот делът просто не съществува и колоната остава с тире.
      shareOfRevenue:
        entry.revenue > 0
          ? (entry.total / (entry.revenue + entry.total)) * 100
          : null,
    }))
    .sort((a, b) => b.total - a.total);
};

/** Ред в изгледа „По продукт". */
export type ProductVoidRow = {
  itemId: number;
  stationId: number;
  rows: number;
  quantity: number;
  afterKitchen: number;
  total: number;
};

export const groupByProduct = (items: CancelledItem[]): ProductVoidRow[] => {
  const map = new Map<number, ProductVoidRow>();

  for (const item of items) {
    const entry = map.get(item.item_id) ?? {
      itemId: item.item_id,
      stationId: item.station_id,
      rows: 0,
      quantity: 0,
      afterKitchen: 0,
      total: 0,
    };
    entry.rows += 1;
    entry.quantity += parseDecimal(item.quantity);
    if (item.after_kitchen === true) entry.afterKitchen += 1;
    entry.total += parseDecimal(item.total_sum);
    map.set(item.item_id, entry);
  }

  return Array.from(map.values()).sort((a, b) => b.total - a.total);
};

/**
 * Позицията на реда в партидата му — по нея се рисуват белезите „направено
 * заедно" (скоба, чип, притъмняло име, общ фон).
 *
 * `single` = сам по себе си; `first`/`middle`/`last` = част от партида, чиито
 * съседи стоят точно до него; `lone` = част от партида, но съседите ѝ в този
 * изглед са другаде (друга подредба, филтър, друга страница) — тогава има само
 * чип „×N заедно", без скоба и без общ фон, които иначе биха свързали чужди
 * редове.
 */
export type BatchPosition = "single" | "first" | "middle" | "last" | "lone";

/**
 * Позициите се смятат върху РЕДОВЕТЕ, КОИТО ТАБЛИЦАТА РИСУВА, а не върху реда
 * от сървъра: скобата свързва СЪСЕДНИ редове, а таблицата се сортира и
 * филтрира в браузъра. Скоба, смятана върху сървърната подредба, при сортиране
 * по сума би свързала редове, които вече не стоят един до друг.
 *
 * Кешът е по референцията на масива (TanStack мемоизира редовия модел), за да
 * не се обхожда наново при всяка клетка.
 */
const positionCache = new WeakMap<object, Map<number, BatchPosition>>();

type BatchRow = { batch_key?: string; batch_size?: number; id: number };

const batchPositionsOf = (
  rows: readonly BatchRow[],
): Map<number, BatchPosition> => {
  const cached = positionCache.get(rows);
  if (cached) return cached;

  const positions = new Map<number, BatchPosition>();
  for (let index = 0; index < rows.length; index += 1) {
    const item = rows[index];
    if ((item.batch_size ?? 1) <= 1 || !item.batch_key) {
      positions.set(item.id, "single");
      continue;
    }
    const sameAsPrevious = rows[index - 1]?.batch_key === item.batch_key;
    const sameAsNext = rows[index + 1]?.batch_key === item.batch_key;
    positions.set(
      item.id,
      !sameAsPrevious && !sameAsNext
        ? "lone"
        : !sameAsPrevious
          ? "first"
          : sameAsNext
            ? "middle"
            : "last",
    );
  }

  positionCache.set(rows, positions);
  return positions;
};

/**
 * Кешът на „оригиналите" по редовия модел на таблицата. Без него `.map()` в
 * клетката правеше НОВ масив при всеки рендер на всяка клетка — кешът на
 * позициите не улучваше нито веднъж и целият списък се обхождаше отново за
 * всеки ред.
 */
const originalsCache = new WeakMap<object, BatchRow[]>();

/** Позициите върху редовете на ТЕКУЩАТА страница на таблицата. */
export const batchPositionsOfRowModel = (
  rows: readonly { original: BatchRow }[],
): Map<number, BatchPosition> => {
  let originals = originalsCache.get(rows);
  if (!originals) {
    originals = rows.map((row) => row.original);
    originalsCache.set(rows, originals);
  }
  return batchPositionsOf(originals);
};

/**
 * Кои редове носят общия фон на партидата — само тези, чиито съседи са ТУК.
 *
 * Разкъсаната партида (`lone`) остава без фон нарочно: два несъседни реда от
 * различни сторна, паднали един до друг след сортиране, биха се слели в една
 * лента и биха изглеждали като една операция.
 */
export const batchBandRowIds = (rows: readonly BatchRow[]): Set<number> => {
  const banded = new Set<number>();
  for (const [id, position] of batchPositionsOf(rows)) {
    if (position !== "single" && position !== "lone") banded.add(id);
  }
  return banded;
};
